import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Modal, Typography } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"
import { useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { EditCompanyForm } from "./EditCompanyForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type {
  Company,
  CompanyDefaultUnitOfMeasurement,
  CompanyPreferredMeasurementSystemEnum,
  CompanyStatus,
  CompanyUpdateFailure,
} from "@/graphql/codegen/graphql"
import { CompanyDuplicateConfirmationDialog } from "@/screens/Companies/components/CompanyDuplicateConfirmationDialog"
import { CompanyModalSchema } from "@/screens/Companies/create/components/CreateNewCompanySchema"
import { useCreateNewCompanyModal } from "@/screens/Companies/hooks/useCreateNewCompanyModal"
import { toCompanyFormErrorField } from "@/screens/Companies/utils/company"
import type { DialogCloseAction } from "@/types/common"
import type { TreeNode } from "@/types/tree"
import { deepEqualIgnoreTypename } from "@/utils/deepEqualIgnoreTypename"

interface CompanyContent {
  name: string
  description?: string | null
  types: string[]
  preferredMeasurementSystem: CompanyPreferredMeasurementSystemEnum
  defaultUom?: string | null
  status?: CompanyStatus
}

interface EditCompanyModalProps {
  isOpen: boolean
  data: CompanyContent
  companyId: string
  onClose: (data?: Company, reason?: DialogCloseAction) => void
  onSuccess: (data: Company) => void
}

const EditCompanyModal: FunctionComponent<EditCompanyModalProps> = ({
  isOpen,
  companyId,
  data,
  onClose,
  onSuccess,
}) => {
  const { name, description, types, preferredMeasurementSystem, defaultUom } = data

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const [isDuplicateConfirmationOpen, setIsDuplicateConfirmationOpen] = useState(false)
  const [pendingUpdateData, setPendingUpdateData] = useState<CompanyContent | null>(null)
  const methods = useForm<CompanyContent>({
    values: {
      name,
      description: description ?? "",
      types,
      preferredMeasurementSystem,
      defaultUom: defaultUom ?? "",
    },
    resolver: zodResolver(CompanyModalSchema),
    mode: "onChange",
  })
  const {
    setError,
    handleSubmit,
    formState: { dirtyFields },
  } = methods

  const { companyTypeList, updateCompany, checkCompanyNameDuplicate } = useCreateNewCompanyModal()

  const handleCompanyUpdate = useCallback(
    async (updatedData: CompanyContent) => {
      const hasChanges = !deepEqualIgnoreTypename(updatedData, { ...data, description: data.description ?? "" })

      if (!hasChanges) {
        onClose(data as Company)
        return
      }

      const { data: updateCompanyData, error: updateCompanyError } = await updateCompany({
        input: {
          companyId,
          ...updatedData,
          description: updatedData.description?.trim() || null,
          defaultUom: updatedData?.defaultUom ? (updatedData.defaultUom as CompanyDefaultUnitOfMeasurement) : null,
        },
      })

      const requestError =
        (updateCompanyError ?? updateCompanyData?.company.update.__typename === "CompanyUpdateFailure")
          ? (updateCompanyData?.company.update as CompanyUpdateFailure).error
          : null

      if (requestError) {
        setError(toCompanyFormErrorField(requestError.field), {
          type: "server",
          message: requestError?.message || "Failed to update company details",
        })
        console.error(requestError.message)
      } else if (updateCompanyData?.company.update.__typename === "CompanyUpdateSuccess") {
        const companyData = updateCompanyData.company.update.company
        methods.reset({
          name: companyData.name ?? undefined,
          types: companyData.types?.map((type) => type ?? undefined),
        })
        onSuccess(companyData)
        onClose(companyData)
      }
    },
    [companyId, data, onClose, onSuccess, setError, updateCompany, methods]
  )

  const handleDuplicateConfirm = useCallback(async () => {
    setIsDuplicateConfirmationOpen(false)
    if (pendingUpdateData) {
      await handleCompanyUpdate(pendingUpdateData)
      setPendingUpdateData(null)
    }
  }, [pendingUpdateData, handleCompanyUpdate])

  const handleDuplicateCancel = useCallback(() => {
    setIsDuplicateConfirmationOpen(false)
    setPendingUpdateData(null)
  }, [])

  const onSubmit = handleSubmit(async (updatedData) => {
    if (dirtyFields.name) {
      if (await checkCompanyNameDuplicate(updatedData.name, companyId)) {
        setPendingUpdateData(updatedData)
        setIsDuplicateConfirmationOpen(true)
        return
      }
    }

    await handleCompanyUpdate(updatedData)
  })

  const confirmCancel = () => {
    setIsCancelConfirmationOpen(false)
    methods.reset()
    onClose()
  }

  const handleCancel = useCallback(() => {
    setIsCancelConfirmationOpen(true)
  }, [])

  return (
    <>
      {isDuplicateConfirmationOpen && (
        <CompanyDuplicateConfirmationDialog
          confirmLabel='Update Anyway'
          onClose={handleDuplicateCancel}
          onConfirm={handleDuplicateConfirm}
        />
      )}
      <Modal open={isOpen} onClose={handleCancel}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            boxShadow: 24,
            width: 576,
            borderRadius: 2,
          }}
        >
          {isCancelConfirmationOpen ? (
            <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, pl: 5 }}>
                <Typography variant='h6' color='primary'>
                  Edit Company profile
                </Typography>
                <Button size='small' appearance='text' onClick={confirmCancel}>
                  <CloseOutlined />
                </Button>
              </Box>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
              <FormProvider {...methods}>
                <EditCompanyForm
                  companyTypeList={companyTypeList as unknown as TreeNode<string>[]}
                  handleCancel={handleCancel}
                  onSubmit={onSubmit}
                />
              </FormProvider>
            </>
          )}
        </Box>
      </Modal>
    </>
  )
}

export default EditCompanyModal
